import { Component, Input, OnInit } from "@angular/core";
import { CustomerObjectsService } from "../../../customer-objects/customer-objects.service";
import { CustomerObjectDeviceDto } from "../../../../shared/models/customerObject/customer-object-device";
import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { DevicesHierarchicalSelectorComponent } from "../../../../shared/components/devices-hierarchical-selector/devices-hierarchical-selector.component";
import { NewRequestDevice } from "../../../../shared/models/request/new-request/new-request-device";
import { DeviceStatus } from "../../../../shared/enums";
import { forkJoin } from "rxjs";
import { Agreement } from "../../../../shared/models/agreement/agreement";
import { AgreementCustomerDeviceComponent } from "../agreement-customer-device/agreement-customer-device.component";
import { CreateCustomerObjectDeviceDto } from "../../../../shared/models/device/create-customer-object-device.dto";
import { DeviceModelsService } from "../../../../shared/services/device-models.service";
import { DeviceModelDto } from "../../../../shared/models/device/device-model-dto";
import { NotificationService } from "../../../../core/services/notification.service";
import { AgreementNonMainDevicesWarningComponent } from "../agreement-non-main-devices-warning/agreement-non-main-devices-warning.component";

@Component({
    selector: 'agreement-devices',
    templateUrl: './agreement-devices.component.html',
    styleUrls: [
        './agreement-devices.component.scss',
        '../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
        '../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
    ]
})
export class AgreementDevicesComponent implements OnInit {

    private static readonly notMainDeviceMessage = 'Выбранный товар не является основным устройством.';

    @Input() agreement: Agreement;
    @Input() isReadonly: boolean = false;

    devices: CustomerObjectDeviceDto[] = [];
    deviceModels: DeviceModelDto[] = [];
    deviceModelsLoaded: boolean = false;

    loading: boolean = false;

    constructor(
        private customerObjectsService: CustomerObjectsService,
        private deviceModelsService: DeviceModelsService,
        private notificationService: NotificationService,
        private dialogService: DialogService,
    ) { }

    ngOnInit(): void {
        this.loadCustomerObjectDevices();
        this.deviceModelsService.getAll(true).subscribe(models => {
            this.deviceModels = models;
            this.deviceModelsLoaded = true;
        });
    }

    loadCustomerObjectDevices() {
        this.loading = true;
        this.customerObjectsService.getCustomerObjectDevices(this.agreement.customerObjectId).subscribe(resp => {
            this.devices = resp.filter(x => x.isMainDevice);
            this.loading = false;
        });
    }

    removeDeviceHandler({ dataItem }) {
        this.loading = true;
        this.customerObjectsService.deleteCustomerObjectDevice(dataItem.customerObjectDeviceId).subscribe(() => {
            this.loadCustomerObjectDevices();
        });
    }

    devicesGridSelectedKeysChange(ids: number[]): void {
        let requestCategoryIds = this.devices.filter(x => ids.some(i => i == x.customerObjectDeviceId)).map(x => x.requestCategoryId);
        requestCategoryIds = requestCategoryIds.filter((value, index, self) => self.indexOf(value) === index);

        //this.agreement.hasConflictDevices = requestCategoryIds.length > 1;
    }

    addNewCatalogDevice() {
        const dialogRef = this.dialogService.open({
            content: DevicesHierarchicalSelectorComponent,
            title: 'Добавление оборудования',
            width: '80%',
            height: '870px',
            minWidth: '800px'
        });
        const devicesSelector = <DevicesHierarchicalSelectorComponent>dialogRef.content.instance;

        devicesSelector.height = 500;

        devicesSelector.onCancel.subscribe(() => {
            dialogRef.close();
        });
        devicesSelector.onContinue.subscribe((requestDevices: NewRequestDevice[]) => {
            this.ensureDeviceModelsLoaded(() => {
                const mainDevices = requestDevices.filter(x => this.isMainDeviceModel(x.deviceModelId));
                const nonMainDevices = requestDevices.filter(x => !this.isMainDeviceModel(x.deviceModelId));

                if (nonMainDevices.length === 0) {
                    this.addCatalogDevices(dialogRef, mainDevices);
                    return;
                }

                this.openNonMainDevicesWarning(nonMainDevices, () => {
                    this.addCatalogDevices(dialogRef, mainDevices);
                });
            });
        });
    }

    addNewCustomerDevice() {
        const dialogRef = this.dialogService.open({
            content: AgreementCustomerDeviceComponent,
            title: 'Добавление оборудования',
            width: '50%',
            height: '640px',
        });
        const devicesSelector = <AgreementCustomerDeviceComponent>dialogRef.content.instance;

        devicesSelector.onCancelEvent.subscribe(() => {
            dialogRef.close();
        });
        devicesSelector.onContinueEvent.subscribe((device: CreateCustomerObjectDeviceDto) => {
            this.ensureDeviceModelsLoaded(() => {
                if (!this.isMainDeviceModel(device.deviceModelId)) {
                    this.notifyNotMainDevice();
                    return;
                }

                dialogRef.close();
                this.loading = true;
                device.deviceStatusId = <number>DeviceStatus.installed;
                device.ownerContragentId = this.agreement.contragentId;

                this.customerObjectsService.createCustomerObjectDevice(this.agreement.customerObjectId, device).subscribe(() => {
                    this.loadCustomerObjectDevices();
                });
            });
        });
    }

    public clientsGridGridSelectedCallback = (args) => args.dataItem.customerObjectDeviceId;

    private addCatalogDevices(dialogRef: DialogRef, mainDevices: NewRequestDevice[]): void {
        dialogRef.close();

        if (mainDevices.length === 0) {
            return;
        }

        this.loading = true;
        const createDevices$ = mainDevices.map(x => {
            let device = new CreateCustomerObjectDeviceDto;
            device.deviceModelId = x.deviceModelId;
            device.deviceTypeId = x.deviceTypeId;
            device.deviceStatusId = <number>DeviceStatus.installed;
            device.ownerContragentId = this.agreement.contragentId;

            return this.customerObjectsService.createCustomerObjectDevice(this.agreement.customerObjectId, device);
        });

        forkJoin(createDevices$).subscribe(() => {
            this.loadCustomerObjectDevices();
        });
    }

    private openNonMainDevicesWarning(nonMainDevices: NewRequestDevice[], onConfirm: () => void): void {
        const warningRef = this.dialogService.open({
            content: AgreementNonMainDevicesWarningComponent,
            width: '520px'
        });
        const warning = <AgreementNonMainDevicesWarningComponent>warningRef.content.instance;
        warning.items = this.getUniqueNonMainDeviceNames(nonMainDevices);
        warning.onCancelEvent.subscribe(() => {
            warningRef.close();
        });
        warning.onConfirmEvent.subscribe(() => {
            warningRef.close();
            onConfirm();
        });
    }

    private getUniqueNonMainDeviceNames(devices: NewRequestDevice[]): string[] {
        const names: string[] = [];
        const seen = new Set<string>();

        devices.forEach(device => {
            const name = this.getCatalogDeviceName(device);
            const key = `${device.deviceModelId}|${device.articleName}|${device.nomenclature}`;
            if (seen.has(key)) {
                return;
            }

            seen.add(key);
            names.push(name);
        });

        return names;
    }

    private getCatalogDeviceName(device: NewRequestDevice): string {
        const sku = (device.articleName || '').trim();
        const name = (device.nomenclature || device.deviceModelName || '').trim();
        if (sku && name) {
            return `${sku} ${name}`;
        }

        return name || sku || 'Без названия';
    }

    private ensureDeviceModelsLoaded(action: () => void): void {
        if (this.deviceModelsLoaded) {
            action();
            return;
        }

        this.deviceModelsService.getAll(true).subscribe(models => {
            this.deviceModels = models;
            this.deviceModelsLoaded = true;
            action();
        });
    }

    private isMainDeviceModel(deviceModelId: number): boolean {
        if (!deviceModelId) {
            return true;
        }

        const model = this.deviceModels.find(x => x.id === deviceModelId);
        return model != null && model.isMainDevice === true;
    }

    private notifyNotMainDevice(): void {
        this.notificationService.error({
            title: 'Оборудование',
            message: AgreementDevicesComponent.notMainDeviceMessage
        });
    }
}
